<template>
    <v-form @submit.prevent="saveDashboardTheme" ref="form">
        <v-card dense class="rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">
                    Customize My Dashboard
                </span>
                <v-spacer></v-spacer>
                <v-btn icon @click.native="closeDialog" :disabled="disabled">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support@sweldonow.ph.
                Error details: {{ errorMessage }}
            </v-alert>

            <v-card-text>
                <!-- 
                As of May 28, 2025, dashboard customization is per theme option only.
                Initial goal is to display widgets/items based on team roles. 
                Eg if Representative role for one team, no need to display Recent Time Logs.
                Further improvements such as per widget customization, drag and drop will be developed if necessary
                -->
                <v-layout row wrap class="form-content">
                    <v-flex xs12>
                        <v-autocomplete
                            v-model="themeId"
                            :disabled="disabled"
                            :items="themes"
                            :rules="[rules.required]"
                            :hint="selectedThemeDescription"
                            persistent-hint
                            chips
                            color="primary lighten-2"
                            label="Select Theme"
                            item-text="themeName"
                            item-value="themeId"
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    {{ data.item.themeName }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.themeName"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                    
                    <v-flex v-if="themeId" xs12 class="mt-20">
                        <v-layout row>
                            <v-flex xs12>
                                My Dashboard Preview
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap class="form-border">
                            <v-flex xs12 sm8 class="form-border">
                                <div
                                    v-for="widget in largeWidgets"
                                    :key="widget.widgetId"
                                    class="pa-1 mr-5"
                                >
                                    <v-chip label class="wp-100">{{ widget.widgetName }}</v-chip>
                                </div>
                            </v-flex>
                            <v-flex xs12 sm4 class="form-border">
                                <div
                                    v-for="widget in smallWidgets"
                                    :key="widget.widgetId"
                                    class="pa-1 mr-5"
                                >
                                    <v-chip label class="wp-100">{{ widget.widgetName }}</v-chip>
                                </div>
                            </v-flex>
                        </v-layout>
                    </v-flex>

                    <v-alert v-if="themeId" :value="true" color="info" icon="info" outline class="mt-10">
                        Your dashboard theme updates automatically when Team Roles or Attendance Settings change. Don't worry—you can still switch back to your favorite theme whenever you like!              
                    </v-alert>  
                </v-layout>

            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" type="submit" :loading="loading" :disabled="disabled||!themeId||!themeUpdated" class="mr-25">
                    Save
                </v-btn>
            </v-card-actions>

        </v-card>
    </v-form>
</template>

<script>

import myDashboarThemeApi from '@/api/mydashboardthemes'

export default {
    data() {
        return {
            loading: false,
            disabled: false,
            error: false,
            errorMessage: '',
            rules: {
                required: value => !!value || 'Required.'
            },
            themeId: null,

            //static data only for faster rendering. no plans to add theme or widgets as of this writing
            themes: [
                {
                    themeId: 1,
                    themeCode: "STAFF",
                    themeName: "Staff Theme",
                    description: "Dashboard for employees focused on attendance and filing requests.",
                    isSystem: true,
                    isActive: true,
                    widgets: [
                        {
                            widgetCode: "RECENT_TIME_LOGS",
                            widgetName: "Recent Time Logs",
                            description: "Displays recent attendance logs, missing logs, late indicators, and timesheet reminders.",
                            widgetSize: "LARGE",
                            rowNumber: 1,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_FAVORITES",
                            widgetName: "My Favorites",
                            description: "Displays user bookmarked application pages.",
                            widgetSize: "SMALL",
                            rowNumber: 1,
                            displayOrder: 2,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_REQUESTS",
                            widgetName: "My Requests",
                            description: "Displays employee filed requests such as leave, overtime, attendance corrections, reimbursements, and cash advances.",
                            widgetSize: "LARGE",
                            rowNumber: 2,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "QUICK_ACTIONS",
                            widgetName: "Quick Actions",
                            description: "Displays shortcuts for filing requests, clocking in/out, and accessing frequently used actions.",
                            widgetSize: "SMALL",
                            rowNumber: 2,
                            displayOrder: 2,
                            isVisible: true
                        }
                    ]
                },
                {
                    themeId: 2,
                    themeCode: "SUPERVISOR",
                    themeName: "Supervisor Theme",
                    description: "Dashboard for supervisors handling approvals and team monitoring.",
                    isSystem: true,
                    isActive: true,
                    widgets: [
                        {
                            widgetCode: "MY_APPROVALS",
                            widgetName: "My Approvals",
                            description: "Displays pending approvals for leave, overtime, attendance corrections, reimbursements, and other requests.",
                            widgetSize: "LARGE",
                            rowNumber: 1,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "QUICK_ACTIONS",
                            widgetName: "Quick Actions",
                            description: "Displays shortcuts for filing requests, clocking in/out, and accessing frequently used actions.",
                            widgetSize: "SMALL",
                            rowNumber: 1,
                            displayOrder: 2,
                            isVisible: true
                        },
                        {
                            widgetCode: "RECENT_TIME_LOGS",
                            widgetName: "Recent Time Logs",
                            description: "Displays recent attendance logs, missing logs, late indicators, and timesheet reminders.",
                            widgetSize: "LARGE",
                            rowNumber: 2,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_FAVORITES",
                            widgetName: "My Favorites",
                            description: "Displays user bookmarked application pages.",
                            widgetSize: "SMALL",
                            rowNumber: 2,
                            displayOrder: 2,
                            isVisible: true
                        }
                    ]
                },
                {
                    themeId: 3,
                    themeCode: "MANAGER",
                    themeName: "Manager Theme",
                    description: "Dashboard for managers supervising multiple teams and approvals.",
                    isSystem: true,
                    isActive: true,
                    widgets: [
                        {
                            widgetCode: "MY_APPROVALS",
                            widgetName: "My Approvals",
                            description: "Displays pending approvals for leave, overtime, attendance corrections, reimbursements, and other requests.",
                            widgetSize: "LARGE",
                            rowNumber: 1,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_FAVORITES",
                            widgetName: "My Favorites",
                            description: "Displays user bookmarked application pages.",
                            widgetSize: "SMALL",
                            rowNumber: 1,
                            displayOrder: 2,
                            isVisible: true
                        },
                        {
                            widgetCode: "QUICK_ACTIONS",
                            widgetName: "Quick Actions",
                            description: "Displays shortcuts for filing requests, clocking in/out, and accessing frequently used actions.",
                            widgetSize: "SMALL",
                            rowNumber: 4,
                            displayOrder: 1,
                            isVisible: true
                        }
                    ]
                },
                {
                    themeId: 4,
                    themeCode: "REPRESENTATIVE",
                    themeName: "Representative Theme",
                    description: "Dashboard for clients, auditors, and representatives focused on approvals and reports.",
                    isSystem: true,
                    isActive: true,
                    widgets: [
                        {
                            widgetCode: "MY_APPROVALS",
                            widgetName: "My Approvals",
                            description: "Displays pending approvals for leave, overtime, attendance corrections, reimbursements, and other requests.",
                            widgetSize: "LARGE",
                            rowNumber: 1,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_FAVORITES",
                            widgetName: "My Favorites",
                            description: "Displays user bookmarked application pages.",
                            widgetSize: "SMALL",
                            rowNumber: 1,
                            displayOrder: 2,
                            isVisible: true
                        }
                    ]
                },
                {
                    themeId: 5,
                    themeCode: "HR",
                    themeName: "HR Theme",
                    description: "Dashboard for HR users with organization-wide visibility, approvals, workforce monitoring, and HR operations.",
                    isSystem: true,
                    isActive: true,
                    widgets: [
                        {
                            widgetCode: "MY_APPROVALS",
                            widgetName: "My Approvals",
                            description: "Displays pending approvals for leave, overtime, attendance corrections, reimbursements, and other requests.",
                            widgetSize: "LARGE",
                            rowNumber: 1,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "QUICK_ACTIONS",
                            widgetName: "Quick Actions",
                            description: "Displays shortcuts for filing requests, clocking in/out, and accessing frequently used actions.",
                            widgetSize: "SMALL",
                            rowNumber: 1,
                            displayOrder: 2,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_REQUESTS",
                            widgetName: "My Requests",
                            description: "Displays employee filed requests such as leave, overtime, attendance corrections, reimbursements, and cash advances.",
                            widgetSize: "LARGE",
                            rowNumber: 4,
                            displayOrder: 1,
                            isVisible: true
                        },
                        {
                            widgetCode: "MY_FAVORITES",
                            widgetName: "My Favorites",
                            description: "Displays user bookmarked application pages.",
                            widgetSize: "SMALL",
                            rowNumber: 4,
                            displayOrder: 2,
                            isVisible: true
                        }
                    ]
                }
            ]
        }
    },
    methods: {
        async saveDashboardTheme() {
            if (!this.$refs.form.validate())
                return

            try {
                this.loading = true
                this.disabled = true

                const response = await myDashboarThemeApi.updateUserTheme(this.themeId)
                if (response && response.status == 200) {
                    //update cache
                    this.$store.commit('setDashboardTheme', response.data)
                    this.closeDialog()
                }

            } catch (error) {
                this.errorMessage = error
                this.error = true

            } finally {
                this.loading = false
                this.disabled = false
            }
        },
        closeDialog() {
            this.$emit('update:dialogCustomizeDashboard', false)
        }
    },
    created() {
        this.themeId = this.savedThemeId
    },
    computed: {
        selectedThemeDescription() {
            if (this.themeId) {
                return this.themes.find(x => x.themeId == this.themeId).description
            }
        },
        selectedWidgets() {
            if (this.themeId) {
                return this.themes.find(x => x.themeId == this.themeId).widgets
            }
        },
        smallWidgets() {
            if (this.selectedWidgets && this.selectedWidgets.length) {
                return this.selectedWidgets.filter(x => x.widgetSize == 'SMALL')
            }
        },
        largeWidgets() {
            if (this.selectedWidgets && this.selectedWidgets.length) {
                return this.selectedWidgets.filter(x => x.widgetSize == 'LARGE')
            }
        },
        savedThemeId() {
            if (this.$store.state.dashboardTheme && this.$store.state.dashboardTheme.theme) {
                return this.$store.state.dashboardTheme.theme.themeId
            }
        },
        themeUpdated() {
            return this.themeId != this.savedThemeId
        }
    }
}
</script>
<style scoped>
.form-content {
    margin: 0px 20px 0px 20px;
}
.form-border {
    border:1px solid lightgrey;
}
</style>